import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, RefreshCw } from 'lucide-react';
import { MainLayout } from '../../../core/layouts/MainLayout';

interface ServerErrorPageProps {
    retry?: () => void;
    withLayout?: boolean;
}

export const ServerErrorPage = ({ retry, withLayout = true }: ServerErrorPageProps) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    const Content = () => (
        <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-20 bg-surface relative overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-red-500 blur-[100px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-secondary blur-[100px]" />
            </div>

            <div className="relative z-10 text-center max-w-2xl mx-auto space-y-8 animate-fade-in-up">
                {/* Large 500 Text */}
                <div className="relative inline-block">
                    <h1 className="text-[120px] sm:text-[180px] font-display font-bold leading-none text-transparent bg-clip-text bg-gradient-to-br from-red-500 via-secondary to-red-500 opacity-20 select-none">
                        500
                    </h1>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full">
                        <h2 className="text-3xl sm:text-4xl font-display font-bold text-secondary uppercase tracking-widest">
                            {t('error.title', 'Server Error')}
                        </h2>
                    </div>
                </div>

                {/* Description */}
                <p className="text-text-muted text-lg sm:text-xl font-light max-w-lg mx-auto leading-relaxed">
                    {t('error.description', "Something went wrong on our end. Please try again later or contact support if the issue persists.")}
                </p>

                {/* Action Buttons */}
                <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <Link
                        to="/"
                        className="group relative inline-flex items-center gap-3 px-8 py-4 bg-white border border-slate-200 text-secondary overflow-hidden rounded-sm transition-all hover:bg-slate-50 hover:shadow-lg"
                    >
                        {isRTL ? (
                            <ArrowRight className="w-4 h-4 relative z-10" />
                        ) : (
                            <ArrowLeft className="w-4 h-4 relative z-10" />
                        )}
                        <span className="relative z-10 font-bold tracking-wider uppercase text-sm">
                            {t('notFound.home', 'Back to Home')}
                        </span>
                    </Link>

                    {retry && (
                        <button
                            onClick={retry}
                            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-secondary text-white overflow-hidden rounded-sm transition-all hover:bg-secondary/90 hover:shadow-lg hover:shadow-secondary/20"
                        >
                            <span className="relative z-10 font-bold tracking-wider uppercase text-sm">
                                {t('common.retry', 'Try Again')}
                            </span>
                            <RefreshCw className="w-4 h-4 relative z-10 transition-transform group-hover:rotate-180" />

                            {/* Hover Effect Background */}
                            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    if (withLayout) {
        return (
            <MainLayout forceSolidNavbar={true}>
                <Content />
            </MainLayout>
        );
    }

    return <Content />;
};
